/**
 * Utility for auto-creating simple project items (e.g., doors, windows)
 * when spaces or windows are created.
 *
 * Uses manage_project_items RPC directly via supabase client.
 * Keeps the logic in one place so multiple components can reuse it.
 */

// Centralized master item IDs
export const MASTER_ITEM_IDS = {
  DOOR: 'a65bff93-b4a8-4f78-8e4f-5d370b79f80a', // דלת פנים
  WINDOW: 'e6a6a781-2ab5-4127-bd2a-41fc97687ed9', // חלון
  STAIRS: 'ba9ee39a-35bc-480f-9aad-1a6fd8e3a0a6', // מדרגות לגרם
  ELECTRICAL_CABINET: '31dc4f4d-44b0-44ed-a2f5-d1f91cb58e0b', // ארון חשמל
  COMM_CABINET: 'db15308e-689f-45b2-b145-06b46fc64f17', // ארון תקשורת
  CEILING_GEVES: '6695fc94-ac93-4f1d-b7d4-e3595aaa4560', // תקרת חיפוי גבס
};

// Centralized master space IDs
export const MASTER_SPACE_IDS = {
  STAIRCASE: '9c7446d7-e85d-4f01-9a70-7a0e363b5f92', // גרם מדרגות
};

/**
 * Create a simple project_item linked to a space.
 *
 * @param {object} supabase - Supabase client instance
 * @param {object} params
 * @param {string} params.projectId - Project UUID
 * @param {string} params.masterItemId - Master item UUID to create
 * @param {string} params.spaceId - Space UUID to link the item to
 * @param {number} [params.quantity=1] - Item quantity
 * @param {object} [params.selectedOption=null] - Selected options (e.g., { "פרופיל": "בלגי", "פתיחה": "הזזה" })
 * @returns {Promise<{data, error}>} Supabase RPC result
 */
export async function createAutoProjectItem(
  supabase,
  { projectId, masterItemId, spaceId, quantity = 1, selectedOption = null },
) {
  const { data, error } = await supabase.rpc('manage_project_items', {
    p_project_id: projectId,
    p_items_data: [
      {
        master_item_id: masterItemId,
        space_id: spaceId,
        quantity,
        estimated_cost: null,
        cost_override: null,
        incl_vat: true,
        total_cost: 0,
        selected_option: selectedOption,
      },
    ],
    p_cost_type_override: null,
  });

  if (error) throw error;
  return data;
}

/**
 * Create a project_item and tag it with a source ID in details (e.g., window_id).
 * Used when the item needs to be linked back to its source for cascade deletion.
 *
 * @param {object} supabase - Supabase client instance
 * @param {object} params - Same as createAutoProjectItem params
 * @param {string} detailsKey - Key to store in details (e.g., 'window_id')
 * @param {string} detailsValue - Value to store (e.g., the window UUID)
 * @returns {Promise<object>} RPC result
 */
export async function createAndTagProjectItem(supabase, params, detailsKey, detailsValue) {
  const result = await createAutoProjectItem(supabase, params);
  const itemIds = result?.item_ids || [];
  if (itemIds.length > 0) {
    await supabase
      .from('project_items')
      .update({ details: { [detailsKey]: detailsValue } })
      .in('id', itemIds);
  }
  return result;
}

/**
 * Auto-create a גרם מדרגות (staircase) space for a floor, with dedup guard.
 * Also creates the מדרגות לגרם project item linked to the space.
 *
 * @param {object} supabase - Supabase client instance
 * @param {object} params
 * @param {string} params.projectId - Project UUID
 * @param {string} params.floorId - Floor UUID to link the staircase space to
 * @param {number|null} params.stairCount - Number of steps (stored in item details)
 * @returns {Promise<{spaceId: string|null, created: boolean}>}
 */
export async function autoCreateStaircaseSpace(supabase, { projectId, floorId, stairCount }) {
  // Dedup guard: check if a גרם מדרגות space already exists for this floor
  const { data: existingSpaces, error: checkError } = await supabase
    .from('project_spaces')
    .select('id')
    .eq('floor_id', floorId)
    .eq('master_space_id', MASTER_SPACE_IDS.STAIRCASE)
    .is('deleted_at', null);

  if (checkError) throw checkError;

  if (existingSpaces?.length > 0) {
    // Space already exists — don't create a duplicate
    return { spaceId: existingSpaces[0].id, created: false };
  }

  // Create the staircase space
  const { data: newSpace, error: spaceError } = await supabase
    .from('project_spaces')
    .insert({
      floor_id: floorId,
      master_space_id: MASTER_SPACE_IDS.STAIRCASE,
      custom_space_name: null, // Uses master space name (גרם מדרגות)
    })
    .select('id')
    .single();

  if (spaceError) throw spaceError;

  // Create the מדרגות לגרם project item linked to the new space
  const result = await createAutoProjectItem(supabase, {
    projectId,
    masterItemId: MASTER_ITEM_IDS.STAIRS,
    spaceId: newSpace.id,
    quantity: 1,
  });

  // Tag the item with floor_id and step_count in details
  const itemIds = result?.item_ids || [];
  if (itemIds.length > 0) {
    await supabase
      .from('project_items')
      .update({
        details: {
          floor_id: floorId,
          step_count: stairCount,
        },
      })
      .in('id', itemIds);
  }

  return { spaceId: newSpace.id, created: true };
}

/**
 * Auto-create a תקרת חיפוי גבס (dropped ceiling) project item for a space.
 * Triggered when room_height < floor ceilingHeight.
 * Includes dedup guard — won't create if one already exists for the space.
 *
 * @param {object} supabase - Supabase client instance
 * @param {object} params
 * @param {string} params.projectId - Project UUID
 * @param {string} params.spaceId - Space UUID
 * @param {number} params.quantity - Area in sqm (typically room_size)
 * @returns {Promise<{created: boolean}>}
 */
export async function autoCreateCeilingGeves(supabase, { projectId, spaceId, quantity }) {
  // Dedup guard: check if this space already has a ceiling geves item
  const { data: existing, error: checkError } = await supabase
    .from('project_items')
    .select('id')
    .eq('project_id', projectId)
    .eq('space_id', spaceId)
    .eq('master_item_id', MASTER_ITEM_IDS.CEILING_GEVES)
    .is('deleted_at', null)
    .limit(1);

  if (checkError) throw checkError;
  if (existing?.length > 0) return { created: false };

  await createAutoProjectItem(supabase, {
    projectId,
    masterItemId: MASTER_ITEM_IDS.CEILING_GEVES,
    spaceId,
    quantity: quantity || 1,
  });

  return { created: true };
}

/**
 * Auto-create construction items (ארון חשמל, ארון תקשורת) for a project.
 * These are project-level items with no space (space_id = null).
 * Includes dedup guard to prevent double-creation.
 *
 * @param {object} supabase - Supabase client instance
 * @param {string} projectId - Project UUID
 * @returns {Promise<void>}
 */
export async function autoCreateConstructionItems(supabase, projectId) {
  const cabinetIds = [MASTER_ITEM_IDS.ELECTRICAL_CABINET, MASTER_ITEM_IDS.COMM_CABINET];

  // Dedup guard: check which items already exist for this project
  const { data: existingItems, error: checkError } = await supabase
    .from('project_items')
    .select('master_item_id')
    .eq('project_id', projectId)
    .in('master_item_id', cabinetIds)
    .is('deleted_at', null);

  if (checkError) throw checkError;

  const existingMasterIds = new Set(existingItems?.map(i => i.master_item_id) || []);
  const toCreate = cabinetIds.filter(id => !existingMasterIds.has(id));

  if (toCreate.length === 0) return;

  // Create missing items (space_id = null for project-level construction items)
  for (const masterItemId of toCreate) {
    await createAutoProjectItem(supabase, {
      projectId,
      masterItemId,
      spaceId: null,
      quantity: 1,
    });
  }
}
